import { maybe } from '@passionware/monads';
import { ApiClient } from 'lib/api/ApiClient.types';
import { z } from 'zod';
import {
  ArchiveEnterpriseTeamInput,
  ArchiveEnterpriseWorkspaceInput,
  CreateEnterpriseTeamInput,
  CreateEnterpriseWorkspaceInput,
  EnterpriseApi,
  UpdateEnterpriseTeamInput,
  UpdateEnterpriseWorkspaceInput
} from './enterprise.api';
import {
  enterpriseApiUrl,
  enterpriseListFilterToArchivedParam,
  resolveOrgWorkspaceId
} from './enterprise.api.http.utils';
import {
  enterpriseTeam$,
  enterpriseTeamFromHttp,
  enterpriseWorkspace$,
  enterpriseWorkspaceFromHttp
} from './enterprise.http.schema';

function parseWorkspaces(response: unknown, orgWorkspaceId: string) {
  return z
    .array(enterpriseWorkspace$)
    .parse(response)
    .map(w => enterpriseWorkspaceFromHttp(w, orgWorkspaceId));
}

function parseTeams(response: unknown, orgWorkspaceId: string) {
  return z
    .array(enterpriseTeam$)
    .parse(response)
    .map(t => enterpriseTeamFromHttp(t, orgWorkspaceId));
}

function parseWorkspace(response: unknown, orgWorkspaceId: string) {
  return enterpriseWorkspaceFromHttp(
    enterpriseWorkspace$.parse(response),
    orgWorkspaceId
  );
}

function parseTeam(response: unknown, orgWorkspaceId: string) {
  return enterpriseTeamFromHttp(
    enterpriseTeam$.parse(response),
    orgWorkspaceId
  );
}

export function createEnterpriseApi(client: ApiClient): EnterpriseApi {
  return {
    getWorkspaces: async (organizationId, filter = 'active') => {
      const orgWorkspaceId = resolveOrgWorkspaceId(organizationId);
      const response = await client.get(enterpriseApiUrl('/workspaces'), {
        params: {
          orgWorkspaceId,
          archived: enterpriseListFilterToArchivedParam(filter)
        },
        token: true
      });
      return parseWorkspaces(response, organizationId);
    },

    getTeams: async (query, filter = 'active') => {
      const orgWorkspaceId = resolveOrgWorkspaceId(query.organizationId);
      const response = await client.get(enterpriseApiUrl('/teams'), {
        params: {
          orgWorkspaceId,
          workspaceId: query.workspaceId,
          archived: enterpriseListFilterToArchivedParam(filter)
        },
        token: true
      });
      return parseTeams(response, query.organizationId);
    },

    createWorkspace: async (input: CreateEnterpriseWorkspaceInput) => {
      const orgWorkspaceId = resolveOrgWorkspaceId(input.organizationId);
      const response = await client.post(enterpriseApiUrl('/workspaces'), {
        data: {
          orgWorkspaceId,
          name: input.name,
          iconUrl: maybe.getOrNull(input.iconUrl),
          ...(maybe.isPresent(input.slug) ? { slug: input.slug } : {})
        },
        token: true
      });
      return parseWorkspace(response, input.organizationId);
    },

    updateWorkspace: async (input: UpdateEnterpriseWorkspaceInput) => {
      const orgWorkspaceId = resolveOrgWorkspaceId(input.organizationId);
      const response = await client.patch(enterpriseApiUrl('/workspaces'), {
        data: {
          orgWorkspaceId,
          id: input.id,
          ...(maybe.isPresent(input.name) ? { name: input.name } : {}),
          ...(maybe.isPresent(input.iconUrl)
            ? { iconUrl: maybe.getOrNull(input.iconUrl) }
            : {})
        },
        token: true
      });
      return parseWorkspace(response, input.organizationId);
    },

    archiveWorkspace: async (input: ArchiveEnterpriseWorkspaceInput) => {
      const orgWorkspaceId = resolveOrgWorkspaceId(input.organizationId);
      const response = await client.post(
        enterpriseApiUrl('/workspaces/archive'),
        {
          data: {
            orgWorkspaceId,
            id: input.id,
            archived: maybe.getOrElse(input.archived, true)
          },
          token: true
        }
      );
      return parseWorkspace(response, input.organizationId);
    },

    createTeam: async (input: CreateEnterpriseTeamInput) => {
      const orgWorkspaceId = resolveOrgWorkspaceId(input.organizationId);
      const response = await client.post(enterpriseApiUrl('/teams'), {
        data: {
          orgWorkspaceId,
          workspaceId: input.workspaceId,
          name: input.name,
          iconUrl: maybe.getOrNull(input.iconUrl),
          iconColor: maybe.getOrNull(input.iconColor),
          ...(maybe.isPresent(input.slug) ? { slug: input.slug } : {})
        },
        token: true
      });
      return parseTeam(response, input.organizationId);
    },

    updateTeam: async (input: UpdateEnterpriseTeamInput) => {
      const orgWorkspaceId = resolveOrgWorkspaceId(input.organizationId);
      const response = await client.patch(enterpriseApiUrl('/teams'), {
        data: {
          orgWorkspaceId,
          id: input.id,
          ...(maybe.isPresent(input.name) ? { name: input.name } : {}),
          ...(maybe.isPresent(input.iconUrl)
            ? { iconUrl: maybe.getOrNull(input.iconUrl) }
            : {}),
          ...(maybe.isPresent(input.iconColor)
            ? { iconColor: maybe.getOrNull(input.iconColor) }
            : {})
        },
        token: true
      });
      return parseTeam(response, input.organizationId);
    },

    archiveTeam: async (input: ArchiveEnterpriseTeamInput) => {
      const orgWorkspaceId = resolveOrgWorkspaceId(input.organizationId);
      const response = await client.post(enterpriseApiUrl('/teams/archive'), {
        data: {
          orgWorkspaceId,
          id: input.id,
          archived: maybe.getOrElse(input.archived, true)
        },
        token: true
      });
      return parseTeam(response, input.organizationId);
    }
  };
}
